import { useSetToast } from "@app/components/Toast";
import {
    BooleanEnum,
    CsrdDatapointPatch,
    CsrdDatapointStatus,
    ValidationStatus,
} from "@generated/client/graphql";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CsrdDatapoint } from "../../../types";
import { activeDatapointIdAtom } from "../../ctx";
import { useUpdateDatapoint } from "../../data";
import {
    isBoolDatapoint,
    isNarrativeDatapoint,
    isNumDatapoint,
    isStrDatapoint,
    isTableDatapoint,
} from "../../services";
import { type PillarDatapoint } from "../../types";
import "./i18n";

export type UseDatapointStateType = ReturnType<typeof useDatapointState>;

export function useDatapointState() {
    const [status, setStatus] = useState<
        | "todo"
        | "validated"
        | "checked"
        | "loading"
        | "inProgress"
        | "notMaterial"
    >("todo");
    const [ownerId, setownerId] = useState<string | undefined | null>(
        undefined,
    );
    const [validated, setValidated] = useState<boolean>(false);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
    const [commentIsUpdating, setCommentIsUpdating] = useState<
        boolean | undefined
    >(undefined);
    const [valueIsUpdating, setValueIsUpdating] = useState<boolean | undefined>(
        undefined,
    );
    const [charCount, setCharCount] = useState<number>(0);
    const [comment, setComment] = useState<string | undefined>(undefined);
    const [valueBool, setValueBool] = useState<BooleanEnum | undefined>(
        undefined,
    );
    const [valueNoData, setValueNoData] = useState<boolean | undefined>(
        undefined,
    );
    const [valueString, setValueString] = useState<string | undefined>(
        undefined,
    );
    const [valueNumber, setValueNumber] = useState<number | undefined>(
        undefined,
    );
    const [valueError, setValueError] = useState<string | undefined>(undefined);
    const [valueTable, setValueTable] = useState<
        { key: string; value: string }[] | undefined
    >(undefined);
    const [commentError, setCommentError] = useState<string | undefined>(
        undefined,
    );

    return {
        status,
        ownerId,
        validated,
        isCollapsed,
        commentIsUpdating,
        valueIsUpdating,
        charCount,
        comment,
        valueBool,
        valueString,
        valueNumber,
        valueNoData,
        valueError,
        valueTable,
        commentError,
        setownerId,
        setValueBool,
        setValueNumber,
        setValueString,
        setValueNoData,
        setValueError,
        setValueTable,
        setCommentError,
        setComment,
        setCharCount,
        setIsCollapsed,
        setCommentIsUpdating,
        setValueIsUpdating,
        setStatus,
        setValidated,
    };
}

export function useDatapoint(datapoint: PillarDatapoint) {
    const { mutate } = useUpdateDatapoint(datapoint.id);
    const { setToastSuccess, setToastError } = useSetToast();
    const { t } = useTranslation("FrameworkDatapoint");
    const selectedDatapointId = useAtomValue(activeDatapointIdAtom);
    const datapointState = useDatapointState();
    const {
        setIsCollapsed,
        setStatus,
        setownerId,
        setValidated,
        setCharCount,
        setComment,
        setValueNoData,
        setValueBool,
        setValueString,
        setValueNumber,
        setValueTable,
        setValueIsUpdating,
        setCommentIsUpdating,
    } = datapointState;

    const handleChange = (patch: CsrdDatapointPatch) => {
        if (!datapoint.id) return;
        const isCommentUpdate = patch.comment !== undefined;
        const isValueUpdate =
            patch?.value?.number !== undefined ||
            patch?.value?.string !== undefined ||
            patch?.value?.table !== undefined;
        if (isCommentUpdate) {
            setCommentIsUpdating(true);
        }
        if (isValueUpdate) {
            setValueIsUpdating(true);
        }
        mutate(
            {
                id: datapoint.id,
                patch,
            },
            {
                onSuccess: () => {
                    if (isCommentUpdate) {
                        setCommentIsUpdating(false);
                    }
                    if (isValueUpdate) {
                        setValueIsUpdating(false);
                    }
                    if (!isCommentUpdate && !isValueUpdate) {
                        setToastSuccess(t("update_success"));
                    }
                },
                onError: () => {
                    if (!isCommentUpdate && !isValueUpdate) {
                        setToastError(t("update_error"));
                    }
                },
            },
        );
    };

    function initState(datapoint: CsrdDatapoint) {
        setStatus(
            datapoint?.status === CsrdDatapointStatus.Validated
                ? "validated"
                : datapoint?.status === CsrdDatapointStatus.InProgress
                  ? "inProgress"
                  : datapoint?.status === CsrdDatapointStatus.NotMaterial
                    ? "notMaterial"
                    : "todo",
        );
        setownerId(datapoint?.ownerId ?? undefined);
        setValidated(
            datapoint?.validationStatus === ValidationStatus.Validated,
        );

        setCharCount(datapoint?.comment?.length ?? 0);
        setComment(datapoint?.comment ?? undefined);
        setValueNoData(
            datapoint?.value?.noData === BooleanEnum.True ? true : false,
        );
        setValueBool(
            isBoolDatapoint(datapoint)
                ? (datapoint?.value?.boolean as BooleanEnum)
                : undefined,
        );
        setValueString(
            isStrDatapoint(datapoint) || isNarrativeDatapoint(datapoint)
                ? datapoint?.value?.string
                : undefined,
        );
        setValueNumber(
            isNumDatapoint(datapoint) ? datapoint?.value?.number : undefined,
        );
        setValueTable(
            isTableDatapoint(datapoint) && datapoint.value?.table
                ? datapoint.value?.table.map(
                      (pair: { key: string; value: { string: string } }) => {
                          return {
                              key: pair.key,
                              value: pair.value?.string ?? "",
                          };
                      },
                  )
                : undefined,
        );
    }

    useEffect(() => {
        if (selectedDatapointId === datapoint.cmsId) {
            setIsCollapsed(false);
        }
    }, [selectedDatapointId, datapoint.cmsId]);

    useEffect(() => {
        initState(datapoint);
    }, [datapoint]);

    return { datapointState, handleChange };
}
